import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import type { Room } from 'lumen/components/settings/central-mapping/roomtype-mapping';
import { STANDARD_ROOMS } from 'lumen/utils/constants/general-constants';

interface SettingsCentralMappingFormFieldsAddRoomtypeSignature {
  Args: {
    allRoomSelections: Room[];
    autocompleteDefaultOptions: Room[];
    closeAddColumn: (roomName?: string) => void;
    updateAddedRooms: (rooms: Room) => void;
  };
}

export default class SettingsCentralMappingFormFieldsAddRoomtype extends Component<SettingsCentralMappingFormFieldsAddRoomtypeSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    return this.args.autocompleteDefaultOptions.map((option: Room) => ({
      rid: option.rid,
      label: option.proper_name,
    }));
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildLabelWithSearchText(searchText: string) {
    return this.intl.t('rates.roomtype.create_roomtype', {
      roomname: searchText,
    });
  }

  @action closeAddColumn(): void {
    this.args.closeAddColumn();
  }

  @action
  async onCreateClick(roomName: string): Promise<void> {
    const selections = this.args.allRoomSelections;

    // Check if the name is not a standard roomtype
    const isStandardRoomtypeName =
      STANDARD_ROOMS.indexOf(roomName.toLowerCase()) !== -1;

    if (!isStandardRoomtypeName) {
      // Check to see if the name exists
      const nameExists = selections.some(
        (room) =>
          room.proper_name.toString().toLowerCase() === roomName.toLowerCase(),
      );

      // If it does not exists, remove the empty room (only one empty room at any given time)
      // And add the new roomname
      if (!nameExists) {
        this.args.updateAddedRooms({
          proper_name: roomName,
          name: roomName,
          rid: `c_${roomName.replace(/ /g, '_')}`,
        });
      } else {
        // Else give an error to the user
        await this.alerting.showErrorAlert({
          title: this.intl.t('rates.roomtype.error'),
          text: this.intl.t('rates.roomtype.alert_used_name'),
        });
      }
      this.args.closeAddColumn(roomName);
    } else {
      await this.alerting.showErrorAlert({
        title: this.intl.t('general.error_title'),
        text: this.intl.t('rates.roomtype.alert_predefined_room_type'),
      });
      this.closeAddColumn();
    }
  }

  @action selectRoomtype(option: Option & { rid: string }): void {
    const room = this.args.autocompleteDefaultOptions.find(
      (defaultOption) => defaultOption.rid === option.rid,
    );
    if (room) {
      this.args.updateAddedRooms(room);
    }
    this.args.closeAddColumn(room?.name);
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @isSearchable={{true}}
      @onChange={{this.selectRoomtype}}
      @options={{this.options}}
      @placeholder={{t "settings.central_mapping.add_room_type"}}
      @unsafe_createWhenNoMatchesAction={{hash
        buildLabelWithSearchText=this.buildLabelWithSearchText
        onClick=this.onCreateClick
      }}
      @unsafe_dropdownClass="add-roomtype-dropdown"
    />
  </template>
}
